import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import Icon from '@mdi/react';
import { mdiClose, mdiMagnify } from '@mdi/js';
import { debounce } from 'lodash';
import { TextInput, Button } from '@warehouse/shared/legacy-ui';
import { serviceLogStoreSelector, useServiceLogStore } from '@warehouse/service-log/domain';

const SearchBar = styled(TextInput)(
	({ theme }) => css`
		margin: 0;
		padding: ${theme.spacing(2)};
	`,
);

/** The Object Browser search bar, filtering the log by service name words. */
export function ServiceLogSearchBar() {
	const searchText = useServiceLogStore(serviceLogStoreSelector.searchText);
	const { setSearchText } = useServiceLogStore(serviceLogStoreSelector.actions);
	const [localSearchText, setLocalSearchText] = useState(searchText);
	const debouncedSetSearchText = useMemo(() => debounce(setSearchText, 300), [setSearchText]);

	useEffect(() => () => debouncedSetSearchText.cancel(), [debouncedSetSearchText]);

	const onChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setLocalSearchText(event.target.value);
			debouncedSetSearchText(event.target.value);
		},
		[debouncedSetSearchText],
	);

	const onClear = useCallback(() => {
		debouncedSetSearchText.cancel();
		setLocalSearchText('');
		setSearchText('');
	}, [debouncedSetSearchText, setSearchText]);

	return (
		<SearchBar
			placeholder="Search jobs"
			aria-label="Search jobs"
			startIcon={<Icon path={mdiMagnify} size="24px" />}
			endIcon={
				<Button $disablePadding nxstyle="tertiary-light" aria-label="Clear search" onClick={onClear}>
					<Icon path={mdiClose} size="16px" />
				</Button>
			}
			value={localSearchText}
			onChange={onChange}
			height={32}
		/>
	);
}
